import { useEffect, useState } from 'react';
import { useSession, useUser } from '@clerk/clerk-react';
import * as Y from 'yjs';
import { HocuspocusProvider, HocuspocusProviderConfiguration, WebSocketStatus } from '@hocuspocus/provider';
import { Awareness } from 'y-protocols/awareness';
import { getEnvironmentConfig } from '../../environmentConfig';

export type CollabConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export interface CollabUserInfo {
    userId: string;
    displayName: string;
    color: string;
    avatarUrl: string | null;
}

export interface YjsRoom {
    yDoc: Y.Doc;
    awareness: Awareness;
    undoManager: Y.UndoManager;
    status: CollabConnectionStatus;
    /**
     * `true` once hocuspocus has fired its `synced` event for this provider — i.e.
     * the doc has received its initial state from the server (or has been
     * confirmed empty). Phase 3 gates the editor render on this so we don't show
     * a one-frame empty graph from `yDocToJson({})`.
     */
    synced: boolean;
    userInfo: CollabUserInfo | null;
}

const PALETTE = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];

function colorForUserId(userId: string): string {
    let hash = 0;
    for (let index = 0; index < userId.length; index += 1) {
        hash = (hash * 31 + userId.charCodeAt(index)) >>> 0;
    }
    return PALETTE[hash % PALETTE.length];
}

/**
 * Open a Y.Doc-backed collaboration room for a graph.
 *
 * Phase 2 scope: doc + awareness + per-user undo. Returns null while waiting for
 * Clerk auth or while the underlying socket is still negotiating.
 *
 * The hook owns the lifetime of the doc and provider — destroying both on unmount.
 * Callers must treat `yDoc` as a stable reference between renders only as long as
 * `graphId` does not change.
 */
export function useYjsRoom(graphId: string): YjsRoom | null {
    const { session } = useSession();
    const { user } = useUser();
    const [room, setRoom] = useState<YjsRoom | null>(null);

    useEffect(() => {
        if (!session || !user) return;

        let cancelled = false;
        const yDoc = new Y.Doc();
        const userInfo: CollabUserInfo = {
            userId: user.id,
            displayName: user.fullName ?? user.username ?? user.primaryEmailAddress?.emailAddress ?? 'Anonymous',
            color: colorForUserId(user.id),
            avatarUrl: user.imageUrl ?? null,
        };

        const config: HocuspocusProviderConfiguration = {
            url: getEnvironmentConfig().collabServiceUrl,
            name: graphId,
            document: yDoc,
            // Hocuspocus accepts either string token or () => Promise<string> resolver.
            token: async () => (await session.getToken()) ?? '',
        };

        const provider = new HocuspocusProvider(config);
        const undoManager = new Y.UndoManager(
            [yDoc.getMap('nodes'), yDoc.getMap('connections'), yDoc.getMap('variables'), yDoc.getMap('functions')],
            {
                trackedOrigins: new Set([yDoc.clientID]),
                // Without this, multiple unrelated mutations within yjs's default
                // 500ms grouping window all collapse into one undo step. Setting
                // this matches the default, but explicit so future tweaks (e.g.
                // shorter for snappier per-action undo) live in one place.
                captureTimeout: 500,
            },
        );

        provider.setAwarenessField('user', {
            userId: userInfo.userId,
            displayName: userInfo.displayName,
            color: userInfo.color,
            avatarUrl: userInfo.avatarUrl,
        });

        const initialRoom: YjsRoom = {
            yDoc,
            awareness: provider.awareness!,
            undoManager,
            status: 'connecting',
            synced: false,
            userInfo,
        };
        if (!cancelled) {
            setRoom(initialRoom);
        }

        const handleStatus = ({ status }: { status: WebSocketStatus }) => {
            if (cancelled) return;
            const mapped: CollabConnectionStatus =
                status === WebSocketStatus.Connected ? 'connected' : status === WebSocketStatus.Connecting ? 'connecting' : 'disconnected';
            setRoom(previous => (previous ? { ...previous, status: mapped } : previous));
        };

        const handleSynced = () => {
            if (cancelled) return;
            setRoom(previous => (previous ? { ...previous, synced: true } : previous));
        };

        provider.on('status', handleStatus);
        provider.on('synced', handleSynced);

        return () => {
            cancelled = true;
            provider.off('status', handleStatus);
            provider.off('synced', handleSynced);
            undoManager.destroy();
            provider.destroy();
            yDoc.destroy();
            setRoom(null);
        };
    }, [graphId, session, user]);

    return room;
}
